import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { PoiSelectedEventData } from '../../PoiSelectedEventData';
import { TranslatePipe } from '../pipes/translate.pipe';
import { FavoritePoiService, FavoritePoiAction } from '../services/favorite-poi.service';
import { TranslationService } from '../services/translation.service';
import { ToastService } from '../services/toast.service';
import { FmsService } from '../api/fms.service';
import { FunctionalAreaDetail, Building, StaticPoi, Employee } from '../api/fms.models';
import { SiteService } from '../site-selector/site.service';
import { getConfig } from '../config.service';
import { DefectService } from '../defects/defect.service';
import { CodebookService } from '../defects/codebook.service';
import { DefectUserRoleService } from '../defects/defect-user-role.service';
import { DefectAgendaService } from '../settings/defect-agenda.service';
import { IDefect, ISeverityLevel, IDefectState } from '../defects/defect.models';

type PanelType = 'poi' | 'building' | 'functionalArea' | 'defect' | 'sector' | 'floor' | 'room';

/**
 * Info shown for FMS tree levels without a linked POI (sector/floor/room).
 * Mirrors legacy FmsInfoSector/FmsInfoFloor/FmsInfoRoom panels.
 */
export interface FmsNodeInfo {
	type: 'sector' | 'floor' | 'room';
	id: number;
	name: string;
	description?: string;
	area?: number;
}

/**
 * View model for the defect info panel.
 * Mirrors legacy DefectDetailViewModel fields used by DefectInfoPanel.template.html
 */
interface DefectPanelVm {
	title: string;
	severity: ISeverityLevel | null;
	defectState: IDefectState | null;
	categories: string;
	createdAt: Date;
	description: string;
}

@Component({
	selector: 'app-poi-info-panel',
	standalone: true,
	imports: [CommonModule, TranslatePipe],
	template: `
		<div class="poi-info-panel">
			<!-- Header -->
			<div class="info-panel-title">
				@if (poiIconUrl) {
					<img [src]="poiIconUrl" alt="POI icon" class="poi-icon" />
				}
				<span class="poi-title">{{ getTitle() }}</span>

				<!-- Toolbar with favorite button (FMS-only levels have no POI to favorite) -->
				@if (!isFmsNodePanel()) {
					<div class="info-panel-title-toolbar">
						<button
							class="favorite-poi-button"
							(click)="toggleFavorite()"
							[disabled]="isBusy"
							[title]="getFavoriteTitle()">
							@if (isBusy) {
								<span class="spinner"></span>
							} @else if (isFavorite) {
								<img src="assets/images/star-yellow.png" alt="Remove from favorites" />
							} @else {
								<img src="assets/images/star-outline.png" alt="Add to favorites" />
							}
						</button>
					</div>

					<!-- Navigation button -->
					@if (showRoutingButton) {
						<button
							class="link-btn"
							(click)="navigate()"
							[disabled]="!poi?.globalLocation">
							{{ 'info_panel_navigate_btn' | translate }}
						</button>
					}
				}
			</div>

			<!-- Loading state -->
			@if (isLoading) {
				<div class="info-panel-content">
					<div class="loading-container">
						<span class="spinner"></span>
						<span>{{ 'loading' | translate:'Načítání' }}</span>
					</div>
				</div>
			}

			<!-- Functional Area panel -->
			@if (!isLoading && panelType === 'functionalArea' && functionalAreaData) {
				<div class="info-panel-content">
					<div class="fms-basic-info">
						<!-- Building name -->
						@if (functionalAreaData.buildingName) {
							<div class="fms-basic-info-item">
								<span class="fms-basic-info-item-header">{{ 'farea_detail_building_name' | translate }}:</span>
								<span class="fms-basic-info-item-value">{{ functionalAreaData.buildingName }}</span>
							</div>
						}

						<!-- Name -->
						@if (functionalAreaData.name) {
							<div class="fms-basic-info-item">
								<span class="fms-basic-info-item-header">{{ 'farea_detail_name' | translate }}:</span>
								<span class="fms-basic-info-item-value">{{ functionalAreaData.name }}</span>
							</div>
						}

						<!-- Area -->
						@if (functionalAreaData.area) {
							<div class="fms-basic-info-item">
								<span class="fms-basic-info-item-header">{{ 'farea_area_detail_label' | translate }}:</span>
								<span class="fms-basic-info-item-value">{{ functionalAreaData.area }} m²</span>
							</div>
						}

						<!-- ID -->
						<div class="fms-basic-info-item">
							<span class="fms-basic-info-item-header">Id:</span>
							<span class="fms-basic-info-item-value">{{ functionalAreaData.id }}</span>
						</div>

						<!-- Description -->
						@if (functionalAreaData.description) {
							<div class="fms-basic-info-item">
								<span class="fms-basic-info-item-header">{{ 'farea_detail_description_label' | translate }}:</span>
								<span class="fms-basic-info-item-value note">{{ functionalAreaData.description }}</span>
							</div>
						}

						<!-- Purpose -->
						@if (functionalAreaData.purpose) {
							<div class="fms-basic-info-item">
								<span class="fms-basic-info-item-header">{{ 'farea_detail_purpose' | translate:'Účel' }}:</span>
								<span class="fms-basic-info-item-value">{{ functionalAreaData.purpose }}</span>
							</div>
						}

						<!-- Workplace count -->
						@if (functionalAreaData.workplaceCount) {
							<div class="fms-basic-info-item">
								<span class="fms-basic-info-item-header">{{ 'farea_detail_workplace_count' | translate }}:</span>
								<span class="fms-basic-info-item-value">{{ functionalAreaData.workplaceCount }}</span>
							</div>
						}

						<!-- GPS Longitude -->
						@if (functionalAreaData.longitude) {
							<div class="fms-basic-info-item">
								<span class="fms-basic-info-item-header">{{ 'farea_detail_gps_longitude_label' | translate }}:</span>
								<span class="fms-basic-info-item-value">{{ functionalAreaData.longitude }}</span>
							</div>
						}

						<!-- GPS Latitude -->
						@if (functionalAreaData.latitude) {
							<div class="fms-basic-info-item">
								<span class="fms-basic-info-item-header">{{ 'farea_detail_gps_latitude_label' | translate }}:</span>
								<span class="fms-basic-info-item-value">{{ functionalAreaData.latitude }}</span>
							</div>
						}

						<!-- Manager -->
						@if (functionalAreaData.managerName) {
							<div class="fms-basic-info-item">
								<span class="fms-basic-info-item-header">{{ 'farea_detail_manager' | translate }}:</span>
								<span class="fms-basic-info-item-value">{{ functionalAreaData.managerName }}</span>
							</div>
						}
					</div>

					<div [attr.data-fa-id]="functionalAreaData.id" id="room-label-placeholder"></div>

					<!-- Employees list -->
					@if (functionalAreaData.employees?.length) {
						<div class="employees-list">
							<div class="employees-list-header">{{ 'employees_header_title' | translate }}</div>
							@for (employee of functionalAreaData.employees; track employee.personalNumber) {
								<div class="employees-list-box">
									<div class="employees-list-box-item">
										<span class="employees-list-box-item-header">{{ 'farea_detail_empl_name' | translate }}:</span>
										<span class="employees-list-box-item-value">{{ formatEmployeeName(employee) }}</span>
									</div>
									@if (employee.departmentName) {
										<div class="employees-list-box-item">
											<span class="employees-list-box-item-header">{{ 'farea_detail_empl_department' | translate }}:</span>
											<span class="employees-list-box-item-value">{{ employee.departmentName }}</span>
										</div>
									}
									@if (employee.phone) {
										<div class="employees-list-box-item">
											<span class="employees-list-box-item-header">{{ 'farea_detail_empl_phone' | translate }}:</span>
											<span class="employees-list-box-item-value">{{ employee.phone }}</span>
										</div>
									}
									@if (employee.email) {
										<div class="employees-list-box-item">
											<span class="employees-list-box-item-header">{{ 'employee_email' | translate }}:</span>
											<span class="employees-list-box-item-value">
												<a [href]="'mailto:' + employee.email">{{ employee.email }}</a>
											</span>
										</div>
									}
								</div>
							}
						</div>
					}

					<!-- Equipment list -->
					@if (functionalAreaData.equipments?.length) {
						<div class="equipment-list">
							<div class="equipment-list-header">{{ 'equipment_header_title' | translate }}</div>
							@for (equipment of functionalAreaData.equipments; track equipment.id) {
								<div class="equipment-list-box">
									<div class="equipment-list-box-item">
										<span class="equipment-list-box-item-value">{{ equipment.name }} ({{ equipment.type }})</span>
									</div>
								</div>
							}
						</div>
					}
				</div>
			}

			<!-- Building panel -->
			@if (!isLoading && panelType === 'building' && buildingData) {
				<div class="info-panel-content">
					<div class="fms-basic-info">
						<!-- Code -->
						@if (buildingData.code) {
							<div class="fms-basic-info-item">
								<span class="fms-basic-info-item-header">{{ 'building_detail_id_label' | translate }}:</span>
								<span class="fms-basic-info-item-value">{{ buildingData.code }}</span>
							</div>
						}

						<!-- Name -->
						@if (buildingData.name) {
							<div class="fms-basic-info-item">
								<span class="fms-basic-info-item-header">{{ 'building_detail_name_label' | translate }}:</span>
								<span class="fms-basic-info-item-value">{{ buildingData.name }}</span>
							</div>
						}

						<!-- ID -->
						<div class="fms-basic-info-item">
							<span class="fms-basic-info-item-header">Id:</span>
							<span class="fms-basic-info-item-value">{{ buildingData.id }}</span>
						</div>

						<!-- Description -->
						@if (buildingData.description) {
							<div class="fms-basic-info-item">
								<span class="fms-basic-info-item-header">{{ 'farea_detail_description_label' | translate }}:</span>
								<span class="fms-basic-info-item-value note">{{ buildingData.description }}</span>
							</div>
						}
					</div>

					<!-- Managers list -->
					@if (buildingData.managers?.length) {
						<div class="employees-list">
							<div class="employees-list-header">{{ 'managers' | translate }}</div>
							@for (manager of buildingData.managers; track manager.personalNumber) {
								<div class="employees-list-box">
									<div class="employees-list-box-item">
										<span class="employees-list-box-item-header">{{ 'farea_detail_empl_name' | translate }}:</span>
										<span class="employees-list-box-item-value">{{ formatEmployeeName(manager) }}</span>
									</div>
									@if (manager.email) {
										<div class="employees-list-box-item">
											<span class="employees-list-box-item-header">{{ 'employee_email' | translate }}:</span>
											<span class="employees-list-box-item-value">
												<a [href]="'mailto:' + manager.email">{{ manager.email }}</a>
											</span>
										</div>
									}
									@if (manager.phone) {
										<div class="employees-list-box-item">
											<span class="employees-list-box-item-header">{{ 'farea_detail_empl_phone' | translate }}:</span>
											<span class="employees-list-box-item-value">{{ manager.phone }}</span>
										</div>
									}
								</div>
							}
						</div>
					}

					<!-- Coordinator -->
					@if (buildingData.coordinator) {
						<div class="employees-list">
							<div class="employees-list-header">{{ 'coordinator' | translate }}</div>
							<div class="employees-list-box">
								<div class="employees-list-box-item">
									<span class="employees-list-box-item-header">{{ 'farea_detail_empl_name' | translate }}:</span>
									<span class="employees-list-box-item-value">{{ formatEmployeeName(buildingData.coordinator) }}</span>
								</div>
								@if (buildingData.coordinator.email) {
									<div class="employees-list-box-item">
										<span class="employees-list-box-item-header">{{ 'employee_email' | translate }}:</span>
										<span class="employees-list-box-item-value">
											<a [href]="'mailto:' + buildingData.coordinator.email">{{ buildingData.coordinator.email }}</a>
										</span>
									</div>
								}
								@if (buildingData.coordinator.phone) {
									<div class="employees-list-box-item">
										<span class="employees-list-box-item-header">{{ 'farea_detail_empl_phone' | translate }}:</span>
										<span class="employees-list-box-item-value">{{ buildingData.coordinator.phone }}</span>
									</div>
								}
							</div>
						</div>
					}
				</div>
			}

			<!-- Sector / Floor panel (FMS-only, no POI; legacy FmsInfoSector/FmsInfoFloor) -->
			@if ((panelType === 'sector' || panelType === 'floor') && fmsInfo) {
				<div class="info-panel-content">
					<div class="fms-basic-info">
						<div class="fms-basic-info-item">
							<span class="fms-basic-info-item-header">{{ (panelType === 'sector' ? 'sector_detail_id' : 'floor_detail_id') | translate }}:</span>
							<span class="fms-basic-info-item-value">{{ fmsInfo.id }}</span>
						</div>
						<div class="fms-basic-info-item">
							<span class="fms-basic-info-item-header">{{ (panelType === 'sector' ? 'sector_detail_name' : 'floor_detail_name') | translate }}:</span>
							<span class="fms-basic-info-item-value">{{ fmsInfo.name }}</span>
						</div>
					</div>
				</div>
			}

			<!-- Room panel (FMS-only, no POI; legacy FmsInfoRoom) -->
			@if (panelType === 'room' && fmsInfo) {
				<div class="info-panel-content">
					<div class="fms-basic-info">
						<div class="fms-basic-info-item">
							<span class="fms-basic-info-item-header">{{ 'room_detail_id' | translate }}:</span>
							<span class="fms-basic-info-item-value">{{ fmsInfo.id }}</span>
						</div>
						@if (fmsInfo.area !== undefined) {
							<div class="fms-basic-info-item">
								<span class="fms-basic-info-item-header">{{ 'room_detail_area' | translate }}:</span>
								<span class="fms-basic-info-item-value">{{ fmsInfo.area }}</span>
							</div>
						}
						@if (fmsInfo.description) {
							<div class="fms-basic-info-item">
								<span class="fms-basic-info-item-header">{{ 'room_detail_description' | translate }}:</span>
								<span class="fms-basic-info-item-value note">{{ fmsInfo.description }}</span>
							</div>
						}
					</div>
				</div>
			}

			<!-- Building/FunctionalArea fallback - show basic info when FMS data not loaded -->
			@if (!isLoading && (panelType === 'building' && !buildingData) || (panelType === 'functionalArea' && !functionalAreaData)) {
				<div class="info-panel-content">
					<div class="poi-info-panel-basics">
						<!-- Name -->
						@if (getPoiName()) {
							<div class="poi-info-panel-basics-item">
								<span class="poi-info-panel-basics-item-header">{{ 'poi_detail_name_label' | translate }}:</span>
								<span class="poi-info-panel-basics-item-value">{{ getPoiName() }}</span>
							</div>
						}

						<!-- ID -->
						<div class="poi-info-panel-basics-item">
							<span class="poi-info-panel-basics-item-header">Id:</span>
							<span class="poi-info-panel-basics-item-value">{{ poi?.id }}</span>
						</div>

						<!-- Description -->
						@if (getPoiDescription()) {
							<div class="poi-info-panel-basics-item">
								<span class="poi-info-panel-basics-item-header">{{ 'poi_detail_description_label' | translate }}:</span>
								<span class="poi-info-panel-basics-item-value note">{{ getPoiDescription() }}</span>
							</div>
						}

						<!-- GPS Longitude -->
						@if (poi?.globalLocation?.x) {
							<div class="poi-info-panel-basics-item">
								<span class="poi-info-panel-basics-item-header">{{ 'poi_detail_gps_longitude_label' | translate }}:</span>
								<span class="poi-info-panel-basics-item-value">{{ formatCoordinate(poi?.globalLocation?.x) }}</span>
							</div>
						}

						<!-- GPS Latitude -->
						@if (poi?.globalLocation?.y) {
							<div class="poi-info-panel-basics-item">
								<span class="poi-info-panel-basics-item-header">{{ 'poi_detail_gps_latitude_label' | translate }}:</span>
								<span class="poi-info-panel-basics-item-value">{{ formatCoordinate(poi?.globalLocation?.y) }}</span>
							</div>
						}
					</div>
				</div>
			}

			<!-- Defect panel -->
			@if (!isLoading && panelType === 'defect' && defectVm) {
				<div class="info-panel-content">
					<div class="defect-basic-info">
						<!-- Title -->
						<div class="defect-basic-info-item">
							<span class="defect-basic-info-item-header">{{ 'defectinfo_panel_title_label' | translate }}:</span>
							<span class="defect-basic-info-item-value">{{ defectVm.title }}</span>
						</div>

						<!-- Severity (label hardcoded in legacy, kept hardcoded here) -->
						<div class="defect-basic-info-item">
							<span class="defect-basic-info-item-header">Závažnost:</span>
							@if (getSeverityIconUrl(defectVm.severity?.id)) {
								<img class="defect-severity-icon" [src]="getSeverityIconUrl(defectVm.severity?.id)" alt="" />
							}
							<span class="defect-basic-info-item-value">{{ defectVm.severity?.title }}</span>
						</div>

						<!-- State -->
						<div class="defect-basic-info-item">
							<span class="defect-basic-info-item-header">{{ 'defectinfo_panel_state_label' | translate }}:</span>
							<span class="defect-basic-info-item-value">{{ defectVm.defectState?.title }}</span>
						</div>

						<!-- Category -->
						<div class="defect-basic-info-item">
							<span class="defect-basic-info-item-header">{{ 'defectinfo_panel_category_label' | translate }}:</span>
							<span class="defect-basic-info-item-value">{{ defectVm.categories }}</span>
						</div>

						<!-- Created -->
						<div class="defect-basic-info-item">
							<span class="defect-basic-info-item-header">{{ 'defectinfo_panel_created_label' | translate }}:</span>
							<span class="defect-basic-info-item-value">{{ formatDate(defectVm.createdAt) }}</span>
						</div>

						<!-- Description label row -->
						<div class="defect-basic-info-item">
							<span class="defect-basic-info-item-header">{{ 'defectinfo_panel_description_label' | translate }}:</span>
						</div>

						<!-- Description value row (full width) -->
						<div class="defect-basic-info-item">
							<span class="defect-basic-info-item-value">{{ defectVm.description }}</span>
						</div>
					</div>

					<div class="defect-info-panel-buttons">
						<button class="link-btn" (click)="showDefectDetail()">
							{{ 'defect_info_panel_detail_btn' | translate }}
						</button>
						<button class="link-btn" [disabled]="!isDefectEditable" (click)="editDefect()">
							{{ 'defect_info_panel_edit_btn' | translate }}
						</button>
					</div>
				</div>
			}

			<!-- StaticPoi panel (default) -->
			@if (!isLoading && panelType === 'poi') {
				<div class="info-panel-content">
					<div class="poi-info-panel-basics">
						<!-- Name -->
						@if (staticPoiData?.name || getPoiName()) {
							<div class="poi-info-panel-basics-item">
								<span class="poi-info-panel-basics-item-header">{{ 'poi_detail_name_label' | translate }}:</span>
								<span class="poi-info-panel-basics-item-value">{{ staticPoiData?.name || getPoiName() }}</span>
							</div>
						}

						<!-- Type name (text) -->
						@if (staticPoiData?.poiTypeName) {
							<div class="poi-info-panel-basics-item">
								<span class="poi-info-panel-basics-item-header">{{ 'poi_detail_poi_type_name' | translate }}:</span>
								<span class="poi-info-panel-basics-item-value">{{ staticPoiData.poiTypeName }}</span>
							</div>
						}

						<!-- ID from StaticPoi (small number) or NavVis ID if not available -->
						<div class="poi-info-panel-basics-item">
							<span class="poi-info-panel-basics-item-header">Id:</span>
							<span class="poi-info-panel-basics-item-value">{{ staticPoiData?.id ?? poi?.id }}</span>
						</div>

						<!-- Note/Description -->
						@if (staticPoiData?.note || getPoiDescription()) {
							<div class="poi-info-panel-basics-item">
								<span class="poi-info-panel-basics-item-header">{{ 'poi_detail_description_label' | translate }}:</span>
								<span class="poi-info-panel-basics-item-value note">{{ staticPoiData?.note || getPoiDescription() }}</span>
							</div>
						}

						<!-- GPS Longitude -->
						@if (staticPoiData?.longitude ?? poi?.globalLocation?.x) {
							<div class="poi-info-panel-basics-item">
								<span class="poi-info-panel-basics-item-header">{{ 'poi_detail_gps_longitude_label' | translate }}:</span>
								<span class="poi-info-panel-basics-item-value">{{ formatCoordinate(staticPoiData?.longitude ?? poi?.globalLocation?.x) }}</span>
							</div>
						}

						<!-- GPS Latitude -->
						@if (staticPoiData?.latitude ?? poi?.globalLocation?.y) {
							<div class="poi-info-panel-basics-item">
								<span class="poi-info-panel-basics-item-header">{{ 'poi_detail_gps_latitude_label' | translate }}:</span>
								<span class="poi-info-panel-basics-item-value">{{ formatCoordinate(staticPoiData?.latitude ?? poi?.globalLocation?.y) }}</span>
							</div>
						}
					</div>
				</div>
			}
		</div>
	`,
	styles: [`
		:host {
			display: block;
			height: 100%;
		}

		.poi-info-panel {
			display: flex;
			flex-direction: column;
			height: 100%;
			background: white;
			border-bottom: 1px solid #ccc;
			box-shadow: 0 0 5px 0 #b4b4b4;
			font-family: 'SKODA Next', Arial, sans-serif;
		}

		.info-panel-title {
			display: flex;
			align-items: center;
			border-bottom: 1px #ddd solid;
			background-color: #efeded;
			padding: 5px 10px;
			font-weight: bold;
			min-height: 42px;
			flex-shrink: 0;
		}

		.poi-icon {
			margin-right: 8px;
			max-width: 32px;
			max-height: 32px;
			object-fit: contain;
		}

		.poi-title {
			flex: 0 1 auto;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			margin-right: 8px;
		}

		.info-panel-title-toolbar {
			display: flex;
			flex: 1;
			justify-content: flex-end;
			align-items: center;
			gap: 8px;
		}

		.favorite-poi-button {
			background: none;
			border: none;
			padding: 0;
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			width: 32px;
			height: 32px;
		}

		.favorite-poi-button img {
			width: 24px;
			height: 24px;
		}

		.favorite-poi-button:hover {
			opacity: 0.8;
		}

		.favorite-poi-button:disabled {
			cursor: not-allowed;
			opacity: 0.5;
		}

		.spinner {
			width: 20px;
			height: 20px;
			border: 2px solid #ddd;
			border-top-color: #4BA82E;
			border-radius: 50%;
			animation: spin 0.8s linear infinite;
		}

		@keyframes spin {
			to {
				transform: rotate(360deg);
			}
		}

		.link-btn {
			border: none;
			color: white;
			background-color: #4BA82E;
			padding: 8px 15px;
			min-width: 100px;
			font-size: 14px;
			border-radius: 5px;
			cursor: pointer;
			font-weight: normal;
			text-align: center;
		}

		.link-btn:hover:not(:disabled) {
			background-color: #3d8a25;
		}

		.link-btn:disabled {
			background-color: #ccc;
			cursor: not-allowed;
		}

		.info-panel-content {
			background-color: #f7f7f7;
			padding: 0;
			flex: 1;
			min-height: 0;
			overflow-y: auto;
		}

		.loading-container {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 10px;
			padding: 20px;
			color: #666;
		}

		/* Defect panel styles (ported from legacy defect.infoPanel.scss) */
		.defect-basic-info {
			padding: 10px;
		}

		.defect-basic-info-item {
			display: flex;
			flex-direction: row;
			margin-top: 10px;
		}

		.defect-basic-info-item-header {
			flex: none;
			display: block;
			width: 125px;
			font-weight: 300;
		}

		.defect-basic-info-item-value {
			flex: 1 1 auto;
			display: block;
			margin-right: 10px;
			word-break: break-word;
		}

		.defect-severity-icon {
			width: 20px;
			height: 20px;
			flex: none;
			margin-right: 5px;
			object-fit: contain;
		}

		.defect-info-panel-buttons {
			display: flex;
			margin: 10px;
		}

		.defect-info-panel-buttons .link-btn {
			border-radius: 0;
			font-size: 13px;
			margin-right: 3px;
			padding: 5px;
		}

		/* POI panel styles */
		.poi-info-panel-basics {
			margin: 0;
			padding: 10px;
		}

		.poi-info-panel-basics-item {
			display: flex;
			flex-direction: row;
			margin-top: 10px;
			line-height: 1.4;
		}

		.poi-info-panel-basics-item:first-child {
			margin-top: 0;
		}

		.poi-info-panel-basics-item-header {
			flex: none;
			display: block;
			width: 125px;
			font-weight: 300;
			color: #666;
		}

		.poi-info-panel-basics-item-value {
			flex: 1 1 auto;
			display: block;
			margin-right: 10px;
			word-break: break-word;
		}

		.poi-info-panel-basics-item-value.note {
			white-space: pre-line;
		}

		/* FMS panel styles */
		.fms-basic-info {
			margin: 0;
			padding: 10px;
		}

		.fms-basic-info-item {
			display: flex;
			flex-direction: row;
			margin-top: 10px;
			line-height: 1.4;
		}

		.fms-basic-info-item:first-child {
			margin-top: 0;
		}

		.fms-basic-info-item-header {
			flex: none;
			display: block;
			width: 125px;
			font-weight: 300;
			color: #666;
		}

		.fms-basic-info-item-value {
			flex: 1 1 auto;
			display: block;
			margin-right: 10px;
			word-break: break-word;
		}

		.fms-basic-info-item-value.note {
			white-space: pre-line;
		}

		/* Employees list */
		.employees-list {
			border-top: 1px solid #ddd;
			padding: 10px;
		}

		.employees-list-header {
			font-weight: bold;
			margin-bottom: 10px;
			color: #333;
		}

		.employees-list-box {
			background: white;
			border: 1px solid #ddd;
			border-radius: 4px;
			padding: 10px;
			margin-bottom: 10px;
		}

		.employees-list-box:last-child {
			margin-bottom: 0;
		}

		.employees-list-box-item {
			display: flex;
			flex-direction: row;
			margin-top: 5px;
			line-height: 1.4;
		}

		.employees-list-box-item:first-child {
			margin-top: 0;
		}

		.employees-list-box-item-header {
			flex: none;
			display: block;
			width: 100px;
			font-weight: 300;
			color: #666;
			font-size: 13px;
		}

		.employees-list-box-item-value {
			flex: 1 1 auto;
			display: block;
			font-size: 13px;
		}

		.employees-list-box-item-value a {
			color: #4BA82E;
			text-decoration: none;
		}

		.employees-list-box-item-value a:hover {
			text-decoration: underline;
		}

		/* Equipment list */
		.equipment-list {
			border-top: 1px solid #ddd;
			padding: 10px;
		}

		.equipment-list-header {
			font-weight: bold;
			margin-bottom: 10px;
			color: #333;
		}

		.equipment-list-box {
			background: white;
			border: 1px solid #ddd;
			border-radius: 4px;
			padding: 8px 10px;
			margin-bottom: 5px;
		}

		.equipment-list-box:last-child {
			margin-bottom: 0;
		}

		.equipment-list-box-item-value {
			font-size: 13px;
		}
	`]
})
export class PoiInfoPanelComponent implements OnChanges {
	@Input() poi: PoiSelectedEventData | null = null;
	@Input() fmsInfo: FmsNodeInfo | null = null;
	@Output() navigateEvent = new EventEmitter<PoiSelectedEventData>();
	@Output() closeEvent = new EventEmitter<void>();
	@Output() viewDefectEvent = new EventEmitter<IDefect>();
	@Output() editDefectEvent = new EventEmitter<IDefect>();

	private favoritePoiService = inject(FavoritePoiService);
	private translationService = inject(TranslationService);
	private toastService = inject(ToastService);
	private fmsService = inject(FmsService);
	private siteService = inject(SiteService);
	private defectService = inject(DefectService);
	private codebookService = inject(CodebookService);
	private defectUserRoleService = inject(DefectUserRoleService);
	private defectAgendaService = inject(DefectAgendaService);

	// Panel type and data
	panelType: PanelType = 'poi';
	functionalAreaData: FunctionalAreaDetail | null = null;
	buildingData: Building | null = null;
	staticPoiData: StaticPoi | null = null;
	defectData: IDefect | null = null;
	defectVm: DefectPanelVm | null = null;
	isDefectEditable = false;

	// UI state
	showRoutingButton = false;  // Route navigation is hidden everywhere for now (same as showFindRouteMenu)
	isFavorite = false;
	isBusy = false;
	isLoading = false;
	poiIconUrl: string | null = null;

	ngOnChanges(changes: SimpleChanges): void {
		// FMS-only tree levels (sector/floor/room) carry all data in the input,
		// nothing needs loading
		if (changes['fmsInfo'] && this.fmsInfo) {
			this.panelType = this.fmsInfo.type;
			this.poiIconUrl = 'assets/images/poi-icon.png';
			this.functionalAreaData = null;
			this.buildingData = null;
			this.staticPoiData = null;
			this.defectData = null;
			this.defectVm = null;
			this.isLoading = false;
			return;
		}

		if (changes['poi'] && this.poi) {
			this.loadPoiDetails();
		}
	}

	isFmsNodePanel(): boolean {
		return this.panelType === 'sector' || this.panelType === 'floor' || this.panelType === 'room';
	}

	private async loadPoiDetails(): Promise<void> {
		if (!this.poi) return;

		// Reset data
		this.functionalAreaData = null;
		this.buildingData = null;
		this.staticPoiData = null;
		this.defectData = null;
		this.defectVm = null;
		this.isDefectEditable = false;

		// Determine panel type and icon immediately (no async)
		const siteSettings = this.siteService.selectedSite?.settings;
		const siteId = this.siteService.selectedSite?.id;
		const defectPoiTypeIds = this.defectAgendaService.getDefectPoiTypeIds();

		if (siteSettings && siteId) {
			if (this.poi.poiTypeId !== undefined && defectPoiTypeIds.includes(this.poi.poiTypeId)) {
				this.panelType = 'defect';
				this.poiIconUrl = 'assets/images/poi-icon.png';
			} else if (this.poi.poiTypeId === siteSettings.fmsFunctionalAreaPoiTypeId) {
				this.panelType = 'functionalArea';
				this.poiIconUrl = 'assets/images/poi-icon.png';
			} else if (this.poi.poiTypeId === siteSettings.fmsBuildingPoiTypeId) {
				this.panelType = 'building';
				this.poiIconUrl = 'assets/images/poi-icon.png';
			} else {
				this.panelType = 'poi';
				this.poiIconUrl = this.getPoiIconUrl();
			}
		} else {
			this.panelType = 'poi';
			this.poiIconUrl = this.getPoiIconUrl();
		}

		// Show loading state for FMS data only
		this.isLoading = true;

		// Load favorites and FMS data in parallel
		const promises: Promise<void>[] = [];

		// Check if POI is favorite (parallel)
		if (this.poi.id) {
			promises.push(
				this.favoritePoiService.isFavoritePoi(this.poi.id).then(isFav => {
					this.isFavorite = isFav;
				})
			);
		}

		// Load FMS data based on panel type (parallel)
		if (siteSettings && siteId) {
			if (this.panelType === 'defect') {
				promises.push(this.loadDefect(siteId));
			} else if (this.panelType === 'functionalArea') {
				promises.push(this.loadFunctionalArea(siteId));
			} else if (this.panelType === 'building') {
				promises.push(this.loadBuilding(siteId));
			} else {
				promises.push(this.loadStaticPoi(siteId));
			}
		}

		// Wait for all parallel operations
		await Promise.all(promises);

		this.isLoading = false;
	}

	private async loadFunctionalArea(siteId: number): Promise<void> {
		// Prefer the explicit FMS reference (selections from search / FMS tree),
		// fall back to the marker in the POI description (clicks on the map)
		const fmsId = this.poi?.fmsRef ?? this.fmsService.parseFmsIdFromDescription(
			this.poi?.descriptions?.['en']
		);

		if (fmsId?.type === 'fa') {
			console.log(`[PoiInfoPanel] Loading functional area ${fmsId.id} for site ${siteId}`);
			this.functionalAreaData = await this.fmsService.getFunctionalAreaDetail(siteId, fmsId.id);
		} else {
			console.warn('[PoiInfoPanel] Could not parse FMS ID from description');
		}
	}

	private async loadBuilding(siteId: number): Promise<void> {
		// Prefer the explicit FMS reference (selections from search / FMS tree),
		// fall back to the marker in the POI description (clicks on the map)
		const fmsId = this.poi?.fmsRef ?? this.fmsService.parseFmsIdFromDescription(
			this.poi?.descriptions?.['en']
		);

		if (fmsId?.type === 'building') {
			console.log(`[PoiInfoPanel] Loading building ${fmsId.id} for site ${siteId}`);
			this.buildingData = await this.fmsService.getBuilding(siteId, fmsId.id);
		} else {
			console.warn('[PoiInfoPanel] Could not parse FMS ID from description');
		}
	}

	private async loadStaticPoi(siteId: number): Promise<void> {
		if (!this.poi?.id) return;

		console.log(`[PoiInfoPanel] Loading static POI ${this.poi.id} for site ${siteId}`);
		this.staticPoiData = await this.fmsService.getStaticPoiByPoiId(siteId, this.poi.id);
	}

	private async loadDefect(siteId: number): Promise<void> {
		if (!this.poi?.id) return;

		try {
			// Codebooks resolve the severity / state / category titles
			await firstValueFrom(this.codebookService.loadAllCodebooks());

			console.log(`[PoiInfoPanel] Loading defect for POI ${this.poi.id} on site ${siteId}`);
			const defect = await firstValueFrom(
				this.defectService.getDefectByPoiId(siteId, this.poi.id)
			);

			if (!defect) return;

			this.defectData = defect;
			this.defectVm = this.mapDefectViewModel(defect);
			this.isDefectEditable = this.defectUserRoleService.canEditDefect(defect);
		} catch (error) {
			console.error(`[PoiInfoPanel] Failed to load defect for POI ${this.poi.id}:`, error);
		}
	}

	private mapDefectViewModel(defect: IDefect): DefectPanelVm {
		const categories = [
			this.codebookService.getCategory1ById(defect.category1Id)?.title,
			this.codebookService.getCategory2ById(defect.category2Id)?.title,
			this.codebookService.getCategory3ById(defect.category3Id)?.title,
		].filter((title): title is string => !!title);

		return {
			title: defect.title,
			severity: this.codebookService.getSeverityById(defect.severityLevelId),
			defectState: this.codebookService.getDefectStateById(defect.defectStateId),
			categories: categories.join(', '),
			// createdAt is delivered as unix seconds
			createdAt: new Date(defect.createdAt * 1000),
			description: defect.description,
		};
	}

	getSeverityIconUrl(severityId: number | undefined): string | null {
		switch (severityId) {
			case 1:
				return 'assets/images/defect/severity-high.png';
			case 2:
				return 'assets/images/defect/severity-medium.png';
			case 3:
				return 'assets/images/defect/severity-low.png';
			default:
				return null;
		}
	}

	formatDate(date: Date | undefined): string {
		if (!date) return '';

		const d = new Date(date);
		const day = String(d.getDate()).padStart(2, '0');
		const month = String(d.getMonth() + 1).padStart(2, '0');
		const year = d.getFullYear();
		const hours = String(d.getHours()).padStart(2, '0');
		const minutes = String(d.getMinutes()).padStart(2, '0');
		const seconds = String(d.getSeconds()).padStart(2, '0');
		return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
	}

	showDefectDetail(): void {
		if (!this.defectData) return;
		this.viewDefectEvent.emit(this.defectData);
	}

	editDefect(): void {
		if (!this.isDefectEditable || !this.defectData) return;
		this.editDefectEvent.emit(this.defectData);
	}

	private getPoiIconUrl(): string | null {
		if (!this.poi?.icon) return null;

		const icon = this.poi.icon;

		// If icon is already a full URL, use it directly
		if (icon.startsWith('http://') || icon.startsWith('https://')) {
			return icon;
		}

		// If icon starts with 'poi', use middleware proxy
		if (icon.startsWith('poi')) {
			return `${getConfig().api.middlewareUrl}/api/NavvisImageHandler?imagepath=${icon}`;
		}

		// Otherwise use NavVis URL directly
		const navvisUrl = getConfig().api.navvisUrl.replace(/\/$/, '');
		return `${navvisUrl}/${icon}`;
	}

	getTitle(): string {
		// Defect panel uses a fixed translated header, same as legacy
		if (this.panelType === 'defect') {
			return this.translationService.translate('defectinfo_panel_title');
		}

		// FMS panels use fixed translated headers, same as legacy
		if (this.panelType === 'sector') {
			return this.translationService.translate('sector_detail_title');
		}
		if (this.panelType === 'floor') {
			return this.translationService.translate('floor_detail_title');
		}
		if (this.panelType === 'room') {
			return this.translationService.translate('room_detail_title');
		}
		if (this.panelType === 'functionalArea' && this.functionalAreaData) {
			return this.translationService.translate('farea_detail_title', 'Funkční plocha');
		}
		if (this.panelType === 'building' && this.buildingData) {
			return this.translationService.translate('building_detail_title', 'Budova');
		}

		// Default: use POI title from IVION
		if (!this.poi?.titles) return 'POI';
		return this.poi.titles['cs'] ?? this.poi.titles['en'] ?? this.poi.titles['default'] ?? 'POI';
	}

	getPoiName(): string {
		if (!this.poi?.titles) return '';
		return this.poi.titles['cs'] ?? this.poi.titles['en'] ?? this.poi.titles['default'] ?? '';
	}

	getPoiDescription(): string {
		if (!this.poi?.descriptions) return '';
		return this.poi.descriptions['cs'] ?? this.poi.descriptions['en'] ?? this.poi.descriptions['default'] ?? '';
	}

	formatCoordinate(value: number | undefined): string {
		if (value === undefined) return '';
		return value.toFixed(8);
	}

	formatEmployeeName(employee: Employee): string {
		const parts: string[] = [];
		if (employee.titleBefore) parts.push(employee.titleBefore);
		if (employee.surname) parts.push(employee.surname);
		if (employee.name) parts.push(employee.name);
		if (employee.titleAfter) parts.push(employee.titleAfter);
		return parts.join(' ') || employee.personalNumber || '-';
	}

	async toggleFavorite(): Promise<void> {
		if (this.isBusy || !this.poi?.id) return;

		this.isBusy = true;

		try {
			const result = await this.favoritePoiService.toggleFavorite(this.poi.id);

			if (result.success) {
				this.isFavorite = result.action === FavoritePoiAction.Add;

				// Show toast message
				const msgKey = result.action === FavoritePoiAction.Add
					? 'favorite_poi_add_success_msg'
					: 'favorite_poi_remove_success_msg';
				this.toastService.success(this.translationService.translate(msgKey));
			} else {
				// API returned success: false
				this.toastService.error(this.translationService.translate('favorite_poi_add_remove_error_msg'));
			}
		} catch (error) {
			console.error('[PoiInfoPanel] Failed to toggle favorite:', error);
			this.toastService.error(this.translationService.translate('favorite_poi_add_remove_error_msg'));
		} finally {
			this.isBusy = false;
		}
	}

	navigate(): void {
		if (!this.poi?.globalLocation) return;

		const ivApi = (window as any).IvionApp?.ivApi;
		if (ivApi?.routing?.service) {
			// Use IVION routing API
			ivApi.routing.service.requestRoute({
				destination: {
					x: this.poi.globalLocation.x,
					y: this.poi.globalLocation.y,
					z: this.poi.globalLocation.z
				}
			});
		}

		this.navigateEvent.emit(this.poi);
	}

	getFavoriteTitle(): string {
		return this.translationService.translate(
			this.isFavorite ? 'favorite_poi_remove_title' : 'favorite_poi_add_title'
		);
	}
}
